/**
 * How much of a pot went on the plate.
 *
 * A helping is not always a bowl. Three quarters of a portion is an ordinary
 * amount to eat, and somebody who weighed the whole pot when it came off the
 * hob can weigh their plate too — 150 g of a pot that made four 200 g portions
 * is three quarters of one, and the app should do that division rather than
 * ask them to.
 *
 * So a helping is said one of two ways, and this module turns either into the
 * figures every screen prints and the body the server takes:
 *
 * - **portions** — a multiple of one portion. Works on any pot.
 * - **grams** — only on a pot that was weighed. Without a cooked weight a gram
 *   figure maps to no portion, and `helpingFor` says so by returning `null`
 *   rather than guessing at the raw density.
 *
 * The one rule shared with the server (`portions_of` in
 * `backend/app/models/batch.py`): **the grams are the record, and the portions
 * the pot is charged are `grams / portion_grams` rounded to two decimals.**
 * The optimistic decrement on screen uses the rounded figure so it lands on
 * the same hundredth the server will report back; the calories use the
 * unrounded ratio, because the server scales the entry from its grams and a
 * third of a portion is 150 kcal, not 148.5.
 */

import type { CookedBatch } from "./types";

export type Helping =
  | { kind: "portions"; portions: number }
  | { kind: "grams"; grams: number };

/** One tap on a tile: a whole portion, said as portions. */
export const ONE_PORTION: Helping = { kind: "portions", portions: 1 };

/** The chips a picker offers. Beyond two is a second sitting, not a helping. */
export const HELPING_FRACTIONS: readonly number[] = [0.25, 0.5, 0.75, 1, 1.5, 2];

/**
 * The per-portion figures a pot reports. Both a stored pot (`BatchOut`) and a
 * shared meal (`SharedMealOut`) carry exactly these, which is what lets one
 * picker sit on the pot's own page and on the page a link opens.
 */
export type HelpingSource = Pick<
  CookedBatch,
  | "weighed"
  | "portion_grams"
  | "kcal_per_portion"
  | "protein_g_per_portion"
  | "carbs_g_per_portion"
  | "fat_g_per_portion"
>;

/** A helping, worked out: what to show, what to spend, what to log. */
export interface Helped {
  /** What the pot is charged — two decimals, the same as the server keeps. */
  portions: number;
  /** Grams on the plate. Nought for an unweighed pot, which is how the diary
   *  says "not weighed" (the column is NOT NULL). */
  grams: number;
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  /** "¾ portion", "1½ portions", "150 g" — for the button and the tray. */
  label: string;
}

/** Two decimals, the precision the pot's own count is stored at. */
export const roundPortions = (portions: number): number => Math.round(portions * 100) / 100;

const QUARTERS: Record<number, string> = { 25: "¼", 50: "½", 75: "¾" };

/**
 * ¼, ½, ¾, 1, 1½, 2 — and "0.33" for a helping that is none of those.
 *
 * Glyphs for the quarters because they are what people say; a decimal for the
 * rest because "⅓" invites a precision the two-decimal count does not keep.
 */
export function formatPortions(portions: number): string {
  const rounded = roundPortions(portions);
  const whole = Math.floor(rounded);
  const hundredths = Math.round((rounded - whole) * 100);
  if (hundredths === 0) return String(whole);
  const glyph = QUARTERS[hundredths];
  if (glyph) return `${whole || ""}${glyph}`;
  return String(rounded);
}

export function helpingLabel(helping: Helping): string {
  if (helping.kind === "grams") return `${Math.round(helping.grams)} g`;
  const shown = formatPortions(helping.portions);
  return `${shown} portion${roundPortions(helping.portions) <= 1 ? "" : "s"}`;
}

/**
 * Work a helping out against a pot.
 *
 * `null` for grams against an unweighed pot — the one combination that has no
 * honest answer — so a caller can disable the button rather than log a bowl
 * of something at the wrong density.
 */
export function helpingFor(source: HelpingSource, helping: Helping): Helped | null {
  const per = {
    kcal: source.kcal_per_portion ?? 0,
    protein_g: source.protein_g_per_portion ?? 0,
    carbs_g: source.carbs_g_per_portion ?? 0,
    fat_g: source.fat_g_per_portion ?? 0,
  };
  let ratio: number;
  let grams: number;
  if (helping.kind === "grams") {
    if (!source.weighed || !source.portion_grams) return null;
    ratio = helping.grams / source.portion_grams;
    grams = helping.grams;
  } else {
    ratio = roundPortions(helping.portions);
    grams = (source.portion_grams ?? 0) * ratio;
  }
  const scale = (value: number) => Math.round(value * ratio * 10) / 10;
  return {
    portions: roundPortions(ratio),
    grams: Math.round(grams * 100) / 100,
    kcal: scale(per.kcal),
    protein_g: scale(per.protein_g),
    carbs_g: scale(per.carbs_g),
    fat_g: scale(per.fat_g),
    label: helpingLabel(helping),
  };
}

/**
 * What goes on the wire. One key, never both: the server refuses a body that
 * says how much twice.
 */
export function helpingBody(helping: Helping): { portions?: number; quantity_g?: number } {
  return helping.kind === "grams"
    ? { quantity_g: helping.grams }
    : { portions: roundPortions(helping.portions) };
}
